import type { ExternalMediaProviderId } from "@seavien/commons";
import {getClipPosterUrl, getClipVideoUrl, getYoutubeUrlMetadata} from "@seavien/commons";
import {normalizeImageUrl} from "@/main/common/GenerationUtils";
import { getUserOwnedImageReferenceFromUrl } from "@/main/util/ImageUrlHelper";

export type DiscussionCardMediaItem = {
    /**
     * `clip` is a short-loop clip: `sourceUrl` is the MP4 and `previewImageUrl` is its poster.
     *
     * That split is the point rather than a detail — the card paints the PREVIEW and only fetches the
     * source after a play gesture, so a clip that scrolls past in a feed costs one WebP and no video
     * bytes. It is the same `preload="none"` posture the in-body player has, expressed in the data
     * shape. A `clip` must therefore never be treated as an `image` whose `sourceUrl` can be painted.
     */
    kind: "image" | "iframe" | "clip";
    provider?: ExternalMediaProviderId | null;
    sourceUrl: string;
    previewImageUrl?: string | null;
    /**
     * Intrinsic pixel dimensions of the image this slide paints, or null/absent when unknown.
     *
     * **Null is permanent and legal**, not a rollout state: provider embeds, external hot-linked
     * images, failed resizes, dropped completion messages and everything published before
     * `user_images.width` existed will never have them. The card falls back to the fixed
     * `--feed-card-media-reserved-height` box in that case.
     *
     * Stamped onto `discussions.card_media_items` by the backend on publish/edit
     * (`DiscussionCardMediaService.buildCardMediaItemsJson`); the client never measures them itself.
     */
    width?: number | null;
    height?: number | null;
};

/**
 * Content equality over every field a slide carries (dimensions included, unlike the dedup key: a
 * later-measured height is a real change to what the card paints). Lets a caller that rebuilds the list
 * from the body on every keystroke keep the previous array when nothing changed (AGENTS.md rule 31).
 */
export function areDiscussionCardMediaItemsEqual(a: readonly DiscussionCardMediaItem[], b: readonly DiscussionCardMediaItem[]): boolean {
    if (a === b) return true;
    if (a.length !== b.length) return false;
    return a.every((item, index) => {
        const other = b[index];
        return item.kind === other.kind
            && (item.provider ?? null) === (other.provider ?? null)
            && item.sourceUrl === other.sourceUrl
            && (item.previewImageUrl ?? null) === (other.previewImageUrl ?? null)
            && (item.width ?? null) === (other.width ?? null)
            && (item.height ?? null) === (other.height ?? null);
    });
}

/** A stored dimension, or null. Zero/negative can only be a corrupt measurement, never a ratio. */
function normalizeMediaDimension(value: unknown): number | null {
    if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
        return null;
    }
    return Math.round(value);
}

function normalizeMediaUrl(value: unknown): string | null {
    if (typeof value !== "string") {
        return null;
    }

    const trimmed = value.trim();
    return trimmed ? trimmed : null;
}

export function normalizeDiscussionCardMediaSelectionUrl(value: unknown): string | null {
    const normalizedValue = normalizeMediaUrl(value);
    if (!normalizedValue) {
        return null;
    }

    return getUserOwnedImageReferenceFromUrl(normalizedValue)
        ? normalizeImageUrl(normalizedValue)
        : normalizedValue;
}

function toDiscussionCardMediaItem(value: unknown): DiscussionCardMediaItem | null {
    if (!value || typeof value !== "object") {
        return null;
    }

    const candidate = value as Record<string, unknown>;
    const kind = candidate.kind === "image" || candidate.kind === "iframe" || candidate.kind === "clip"
        ? candidate.kind
        : null;
    const sourceUrl = normalizeMediaUrl(candidate.sourceUrl);
    if (!kind || !sourceUrl) {
        return null;
    }

    const provider = candidate.provider === "youtube"
        || candidate.provider === "redGifs"
        || candidate.provider === "pornHub"
        || candidate.provider === "linkCard"
        ? candidate.provider
        : null;

    return {
        kind,
        provider,
        sourceUrl,
        previewImageUrl: normalizeMediaUrl(candidate.previewImageUrl),
        width: normalizeMediaDimension(candidate.width),
        height: normalizeMediaDimension(candidate.height),
    };
}

/**
 * The dedup identity of a slide. **Deliberately excludes width/height**, and the Java
 * `CardMediaItem.uniqueKey()` does the same: identity here is the URL, so folding a later-measured
 * height into the key would let the same image survive twice in one carousel. Keep the two in step.
 */
function toUniqueKey(item: DiscussionCardMediaItem): string {
    return `${item.kind}|${item.provider ?? ""}|${item.sourceUrl}|${item.previewImageUrl ?? ""}`;
}

function isKnownIframeProviderUrl(value: string): boolean {
    const normalizedValue = value.toLowerCase();
    return normalizedValue.includes("youtube.com")
        || normalizedValue.includes("youtu.be")
        || normalizedValue.includes("youtube-nocookie.com")
        || normalizedValue.includes("redgifs.com")
        || normalizedValue.includes("pornhub.com");
}

export function isDiscussionCardMediaItemSelected(item: DiscussionCardMediaItem, selectedUrl?: unknown): boolean {
    const normalizedSelectedUrl = normalizeDiscussionCardMediaSelectionUrl(selectedUrl);
    if (!normalizedSelectedUrl) {
        return false;
    }

    return normalizeDiscussionCardMediaSelectionUrl(item.sourceUrl) === normalizedSelectedUrl
        || normalizeDiscussionCardMediaSelectionUrl(item.previewImageUrl) === normalizedSelectedUrl;
}

export function getDiscussionCardMediaSelectionUrl(item: DiscussionCardMediaItem): string | null {
    return normalizeDiscussionCardMediaSelectionUrl(item.sourceUrl);
}

function addImageItem(orderedItems: Map<string, DiscussionCardMediaItem>, sourceUrl: unknown, provider: DiscussionCardMediaItem["provider"]) {
    const normalizedSourceUrl = normalizeMediaUrl(sourceUrl);
    if (!normalizedSourceUrl) {
        return;
    }

    const item: DiscussionCardMediaItem = {
        kind: "image",
        provider,
        sourceUrl: normalizedSourceUrl,
        previewImageUrl: normalizedSourceUrl,
    };
    orderedItems.set(toUniqueKey(item), item);
}

/**
 * Mirrors `DiscussionCardMediaService.addClipItem` on the Java side.
 *
 * Reads `attrs.publicId`, not `attrs.src`: the clip node stores an id and DERIVES its poster URL from
 * it, so a stored URL can never drift from the object it addresses. Both URLs are built here for the
 * same reason.
 */
function addClipItem(orderedItems: Map<string, DiscussionCardMediaItem>, clipPublicId: unknown) {
    const normalizedPublicId = normalizeMediaUrl(clipPublicId);
    if (!normalizedPublicId) {
        return;
    }

    const item: DiscussionCardMediaItem = {
        kind: "clip",
        provider: null,
        sourceUrl: getClipVideoUrl(normalizedPublicId),
        previewImageUrl: getClipPosterUrl(normalizedPublicId),
    };
    orderedItems.set(toUniqueKey(item), item);
}

function buildYouTubePreviewImageUrl(sourceUrl: unknown): string | null {
    const normalizedSourceUrl = normalizeMediaUrl(sourceUrl);
    const metadata = normalizedSourceUrl ? getYoutubeUrlMetadata(normalizedSourceUrl) : null;
    return metadata ? `https://i.ytimg.com/vi/${metadata.videoId}/hqdefault.jpg` : null;
}

function addIframeItem(
    orderedItems: Map<string, DiscussionCardMediaItem>,
    provider: Exclude<DiscussionCardMediaItem["provider"], "linkCard" | null | undefined>,
    sourceUrl: unknown,
    previewImageUrl: unknown,
) {
    const normalizedSourceUrl = normalizeMediaUrl(sourceUrl);
    if (!normalizedSourceUrl) {
        return;
    }

    const item: DiscussionCardMediaItem = {
        kind: "iframe",
        provider,
        sourceUrl: normalizedSourceUrl,
        previewImageUrl: normalizeMediaUrl(previewImageUrl),
    };
    orderedItems.set(toUniqueKey(item), item);
}

function visitSourceNode(node: unknown, orderedItems: Map<string, DiscussionCardMediaItem>) {
    if (!node) {
        return;
    }

    if (Array.isArray(node)) {
        node.forEach((child) => visitSourceNode(child, orderedItems));
        return;
    }

    if (typeof node !== "object") {
        return;
    }

    const candidate = node as Record<string, unknown>;
    const type = normalizeMediaUrl(candidate.type);
    const attrs = candidate.attrs && typeof candidate.attrs === "object"
        ? candidate.attrs as Record<string, unknown>
        : null;

    switch (type) {
        case "image":
            addImageItem(orderedItems, attrs?.src, null);
            break;
        case "linkCard":
            addImageItem(orderedItems, attrs?.imageUrl, "linkCard");
            break;
        case "youtube":
            addIframeItem(orderedItems, "youtube", attrs?.src, buildYouTubePreviewImageUrl(attrs?.src));
            break;
        case "redGifs":
            addIframeItem(orderedItems, "redGifs", attrs?.src, null);
            break;
        case "pornHub":
            addIframeItem(orderedItems, "pornHub", attrs?.src, null);
            break;
        case "clip":
            addClipItem(orderedItems, attrs?.publicId);
            break;
        default:
            break;
    }

    visitSourceNode(candidate.content, orderedItems);
}

function reorderDiscussionCardMediaItems(items: readonly DiscussionCardMediaItem[], selectedUrl?: unknown): DiscussionCardMediaItem[] {
    const normalizedSelectedUrl = normalizeDiscussionCardMediaSelectionUrl(selectedUrl);
    if (!normalizedSelectedUrl) {
        return [...items];
    }

    const selectedIndex = items.findIndex((item) => isDiscussionCardMediaItemSelected(item, normalizedSelectedUrl));
    if (selectedIndex === 0) {
        return [...items];
    }

    if (selectedIndex > 0) {
        return [
            items[selectedIndex],
            ...items.slice(0, selectedIndex),
            ...items.slice(selectedIndex + 1),
        ];
    }

    const orderedItems = new Map<string, DiscussionCardMediaItem>();
    if (isKnownIframeProviderUrl(normalizedSelectedUrl)) {
        return [...items];
    }
    addImageItem(orderedItems, normalizedSelectedUrl, null);
    items.forEach((item) => {
        orderedItems.set(toUniqueKey(item), item);
    });
    return Array.from(orderedItems.values());
}

export function buildDraftDiscussionCardMediaItems(input: {
    sourceJson?: string | null;
    coverImageUrl?: string | null;
    preserveSourceOrder?: boolean;
}): DiscussionCardMediaItem[] {
    const orderedItems = new Map<string, DiscussionCardMediaItem>();

    const normalizedSourceJson = normalizeMediaUrl(input.sourceJson);
    if (!normalizedSourceJson) {
        return reorderDiscussionCardMediaItems([], input.coverImageUrl);
    }

    try {
        const parsed = JSON.parse(normalizedSourceJson);
        visitSourceNode(parsed, orderedItems);
    } catch {
        return reorderDiscussionCardMediaItems([], input.coverImageUrl);
    }

    const items = Array.from(orderedItems.values());
    return input.preserveSourceOrder
        ? items
        : reorderDiscussionCardMediaItems(items, input.coverImageUrl);
}

export function parseDiscussionCardMediaItems(raw?: unknown): DiscussionCardMediaItem[] {
    if (!raw) {
        return [];
    }

    const parseItems = (value: unknown): DiscussionCardMediaItem[] => {
        if (!Array.isArray(value)) {
            return [];
        }

        const seen = new Set<string>();
        const items: DiscussionCardMediaItem[] = [];
        value.forEach((entry) => {
            const item = toDiscussionCardMediaItem(entry);
            if (!item) {
                return;
            }

            const uniqueKey = toUniqueKey(item);
            if (seen.has(uniqueKey)) {
                return;
            }

            seen.add(uniqueKey);
            items.push(item);
        });
        return items;
    };

    if (Array.isArray(raw)) {
        return parseItems(raw);
    }

    if (typeof raw === "string") {
        const trimmed = raw.trim();
        if (!trimmed) {
            return [];
        }

        try {
            return parseItems(JSON.parse(trimmed));
        } catch {
            return [];
        }
    }

    return [];
}
